import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { ButtonHTMLAttributes, forwardRef, useRef } from 'react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '~/@/components/ui/tooltip'
import { useIsOverflowing } from '~/hooks/useIsOverflowing'

const buttonVariants = cva(
  'ring-offset-background inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-xs font-normal transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-blue-9 hover:bg-blue-10 text-white',
        secondary: 'bg-gray-12 hover:bg-gray-11 text-white',
        tertiary: 'text-gray-10 hover:bg-gray-2 hover:text-gray-12 bg-white',
        outline: 'border-gray-4 hover:bg-gray-2 hover:text-gray-12 border bg-white',
        ghost: 'text-gray-9 hover:text-gray-11',
        destructive: 'bg-red-9 hover:bg-red-10 text-white',
        secondaryDestructive: 'text-red-10 hover:bg-red-2 border-red-10 border',
        link: 'text-blue-9 active:text-blue-10 underline-offset-4 hover:underline',
        input: 'text-gray-8 rounded disabled:opacity-100',
      },
      size: {
        default: 'h-10 px-4 py-2',
        xs: 'h-7 rounded px-3 py-2',
        sm: 'h-9 rounded px-3',
        lg: 'h-11 rounded px-8',
        icon: 'size-10',
        smallIcon: 'size-8',
        input: 'h-9 px-3 py-2',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  readOnly?: boolean
  // https://www.notion.so/doss-com/Add-tooltip-description-overflow-to-disabled-buttons-2e3f16c5ee3680178e63c217ba7487af?v=1b9f16c5ee36806593ae000c9b7dcb7f&source=copy_link
  showTooltipOnOverflow?: boolean
  disabledMessage?: string
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      endIcon,
      children,
      startIcon,
      asChild = false,
      readOnly = false,
      showTooltipOnOverflow = false,
      disabledMessage,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    const textRef = useRef<HTMLSpanElement>(null)
    const isOverflowing = useIsOverflowing(textRef)
    const shouldShowDisabledTooltip = disabled && disabledMessage
    const shouldShowOverflowTooltip = showTooltipOnOverflow && isOverflowing
    const shouldShowTooltip = shouldShowDisabledTooltip || shouldShowOverflowTooltip

    const hasDisabledMessage = shouldShowDisabledTooltip && disabledMessage
    const hasOverflowText = shouldShowOverflowTooltip && typeof children === 'string'
    const tooltipText: string | null =
      (hasDisabledMessage && disabledMessage) || (hasOverflowText && children) || null

    // Remove pointer events if readonly
    if (readOnly) {
      className = cn(className, 'pointer-events-none')
    }

    const buttonContent = (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={disabled}
        {...props}
      >
        {startIcon}
        {showTooltipOnOverflow && typeof children === 'string' ? (
          <span ref={textRef} className="block truncate">
            {children}
          </span>
        ) : (
          children
        )}
        {endIcon}
      </Comp>
    )

    if (shouldShowTooltip && tooltipText) {
      return (
        <Tooltip open={undefined}>
          <TooltipTrigger asChild>
            <span className="inline-block">{buttonContent}</span>
          </TooltipTrigger>
          <TooltipContent className="z-[9999]">{tooltipText}</TooltipContent>
        </Tooltip>
      )
    }

    return buttonContent
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
